'use client'

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faUserPlus, 
  faHeadset, 
  faLifeRing, 
  faFlask,
  faListCheck,
  faCookie,
  faGear,
  faArrowRightFromBracket,
  faChevronDown,
  faUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons'

interface UserDropdownProps {
  user: {
    name: string
    email: string
    avatar: string
    newVersion?: boolean
  }
}

export function UserDropdown({ user }: UserDropdownProps) {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    const token = localStorage.getItem('auth_token')

    try {
      if (token) {
        await fetch('https://app.canerozen.com/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        })
      }
    } catch (error) {
      console.error('Logout request failed:', error)
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('admin_store_nav_scope')
      setIsOpen(false)
      router.replace('/login')
      router.refresh()
    }
  }

  const menuItems = [
    {
      icon: faUserPlus,
      label: 'Invite Your Team',
      href: '/invite',
      external: false,
    },
    {
      icon: faHeadset,
      label: 'Talk to Sales',
      href: '/sales',
      external: false,
    },
    {
      icon: faLifeRing,
      label: 'Talk to Support',
      href: 'https://support.example.com',
      external: true,
    },
    {
      icon: faFlask,
      label: 'Participate in Research',
      href: 'https://research.example.com',
      external: true,
    },
    {
      type: 'divider' as const,
    },
    {
      icon: faCookie,
      label: 'Cookie Preferences',
      href: '/cookies',
      external: false,
    },
    {
      icon: faListCheck,
      label: 'Kişisel Görev Listesi',
      href: '/pages/users/task',
      external: false,
    },
    {
      icon: faGear,
      label: 'Settings',
      href: '/settings',
      external: false,
    },
    {
      type: 'divider' as const,
    },
    {
      icon: faArrowRightFromBracket,
      label: 'Logout',
      onClick: handleLogout,
      external: false,
      danger: true,
    },
  ]

  return (
    <div className="user-dropdown" ref={dropdownRef}>
      <button 
        className="user-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="user-dropdown__avatar">
          {user.avatar}
        </div>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`user-dropdown__arrow ${isOpen ? 'user-dropdown__arrow--open' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="user-dropdown__menu">
          {/* User Info Header */}
          <div className="user-dropdown__header">
            <div className="user-dropdown__avatar user-dropdown__avatar--large">
              {user.avatar}
            </div>
            <div className="user-dropdown__info">
              <div className="user-dropdown__name">
                {user.name}
                <span className={`user-dropdown__version-badge ${user.newVersion ? 'user-dropdown__version-badge--new' : 'user-dropdown__version-badge--old'}`}>
                  {user.newVersion ? 'v2' : 'v1'}
                </span>
              </div>
              <div className="user-dropdown__email">{user.email}</div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="user-dropdown__items">
            {menuItems.map((item, index) => {
              if (item.type === 'divider') {
                return <div key={index} className="user-dropdown__divider" />
              }

              const className = `user-dropdown__item ${item.danger ? 'user-dropdown__item--danger' : ''}`
              const content = (
                <>
                  <FontAwesomeIcon icon={item.icon} className="user-dropdown__item-icon" />
                  <span className="user-dropdown__item-label">{item.label}</span>
                  {item.external && (
                    <FontAwesomeIcon
                      icon={faUpRightFromSquare}
                      className="user-dropdown__item-external"
                    />
                  )}
                </>
              )

              if (item.onClick) {
                return (
                  <button
                    key={index}
                    type="button"
                    className={className}
                    onClick={item.onClick}
                  >
                    {content}
                  </button>
                )
              }

              return (
                <a
                  key={index}
                  href={item.href}
                  className={className}
                  target={item.external ? '_blank' : undefined}
                  rel={item.external ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
